// ── 카드승인 탭 콘텐츠 ────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from 'react';
import { FilterBar } from './FilterBar';
import {
  fmtNum, fmtDate,
  tblWrap, thStyle, tdStyle, amtTd,
  btnPrimary, btnSecondary, btnEdit, btnDanger, btnTd, btnGroup,
} from './styles';

/**
 * 카드승인 탭 콘텐츠 (업로드 이력 + 카드승인 목록)
 * Props:
 *   cardApps, cardBatches, terminalMap
 *   filterStart, setFilterStart, filterEnd, setFilterEnd
 *   loadCardApps, loadCardBatches
 *   fileRef, uploading, handleFileUpload
 *   setModal
 *   onDeleteBatch   — (batchId) 배치 삭제 핸들러
 *   onDownloadBatch — (batchId, fileName) 배치 다운로드 핸들러
 */
export function TabCards({
  cardApps, cardBatches, terminalMap,
  filterStart, setFilterStart, filterEnd, setFilterEnd,
  loadCardApps, loadCardBatches,
  fileRef, uploading, handleFileUpload,
  setModal,
  onDeleteBatch,
  onDownloadBatch,
}) {
  // ── 승인번호 검색 자동완성 상태 ────────────────────────────────────────────
  const [approvalInput,   setApprovalInput]   = useState('');   // 입력값
  const [suggestions,     setSuggestions]     = useState([]);   // 드롭다운 후보
  const [approvalFilter,  setApprovalFilter]  = useState('');   // 확정된 필터값
  const [dropdownOpen,    setDropdownOpen]    = useState(false);
  const dropdownRef = useRef(null);

  // 입력값 변경 시 후보 계산
  function handleApprovalInput(val) {
    setApprovalInput(val);
    setApprovalFilter('');   // 직접 입력 중에는 확정 필터 해제
    if (!val.trim()) {
      setSuggestions([]);
      setDropdownOpen(false);
      return;
    }
    // cardApps에서 approval_no가 입력값을 포함하는 고유 목록 추출
    const matched = Array.from(
      new Set(
        cardApps
          .map((r) => String(r.approval_no || '').trim())
          .filter((v) => v && v.includes(val.trim()))
      )
    ).slice(0, 10); // 최대 10개
    setSuggestions(matched);
    setDropdownOpen(matched.length > 0);
  }

  // 후보 선택
  function selectSuggestion(val) {
    setApprovalInput(val);
    setApprovalFilter(val);
    setSuggestions([]);
    setDropdownOpen(false);
  }

  // 검색창 초기화
  function clearApproval() {
    setApprovalInput('');
    setApprovalFilter('');
    setSuggestions([]);
    setDropdownOpen(false);
  }

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function onClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // ── 표시할 목록: 승인번호 필터 적용 ──────────────────────────────────────
  const displayApps = approvalFilter
    ? cardApps.filter((r) => String(r.approval_no || '').trim() === approvalFilter)
    : cardApps;

  // 승인 건만 합계 (필터 적용 후 기준)
  const total = displayApps
    .filter((r) => r.transaction_type === '승인')
    .reduce((s, r) => s + Number(r.transaction_amount || 0), 0);

  // 업로드 이력 펼침/접힘 — 기본은 최신 1건만 표시
  const [batchExpanded, setBatchExpanded] = useState(false);
  const visibleBatches = batchExpanded ? cardBatches : cardBatches.slice(0, 1);

  return (
    <>
      {/* 업로드 이력 섹션 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 6 }}>업로드 이력</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
          <button style={btnPrimary}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}>
            {uploading ? '업로드 중...' : '📤 엑셀/CSV 업로드'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button style={{ ...btnSecondary, marginLeft: 'auto' }}
            onClick={() => setModal({ type: 'auditlog', tableName: 'revenue_card_approvals', title: '카드승인' })}>
            이력
          </button>
        </div>

        {/* 업로드 이력 테이블 — 기본 최신 1건, 펼치면 전체 */}
        {cardBatches.length > 0 && (
          <div style={tblWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['업로드일시','파일명','전체','신규','갱신','스킵','업로더',''].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleBatches.map((b) => (
                  <tr key={b.id}>
                    <td style={tdStyle}>{b.uploaded_at}</td>
                    <td style={tdStyle}>{b.file_name}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{b.total_rows}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#15803d' }}>{b.new_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#0369a1' }}>{b.updated_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>{b.skipped_count}</td>
                    <td style={tdStyle}>{b.uploaded_by_name || '-'}</td>
                    {/* CSV 다운로드 + 삭제 버튼 — 오른쪽 정렬 */}
                    <td style={btnTd}>
                      <div style={btnGroup}>
                        {onDownloadBatch && (
                          <button
                            style={btnEdit}
                            onClick={() => onDownloadBatch(b.id, b.file_name)}>
                            CSV
                          </button>
                        )}
                        {onDeleteBatch && (
                          <button style={btnDanger}
                            onClick={() => onDeleteBatch(b.id)}>
                            삭제
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* 펼치기/접기 버튼 — 이력이 2건 이상일 때만 표시 */}
            {cardBatches.length > 1 && (
              <button
                onClick={() => setBatchExpanded((v) => !v)}
                style={{
                  width: '100%', padding: '6px 0', border: 'none',
                  background: 'var(--bg-subtle)', color: 'var(--text-muted)',
                  fontSize: 11, cursor: 'pointer',
                  borderTop: '1px solid var(--border)',
                  borderRadius: '0 0 8px 8px',
                }}>
                {batchExpanded
                  ? '▲ 접기'
                  : `▼ 이전 이력 ${cardBatches.length - 1}건 더 보기`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 카드승인 목록 — 기간 필터 + 승인번호 검색을 한 줄로 통합 */}
      <FilterBar
        filterStart={filterStart} setFilterStart={setFilterStart}
        filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
        onSearch={() => { loadCardApps(); loadCardBatches(); }}
        approvalSearchNode={
          /* 승인번호 자동완성 검색창 — FilterBar 내 구분선 뒤에 인라인으로 표시 */
          <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="text"
              placeholder="승인번호 검색"
              value={approvalInput}
              onChange={(e) => handleApprovalInput(e.target.value)}
              onFocus={() => suggestions.length > 0 && setDropdownOpen(true)}
              style={{
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 9px', fontSize: 12,
                background: 'var(--bg-card)', color: 'var(--text)',
                outline: 'none', width: 150,
              }}
            />
            {/* 입력값 있을 때 × 버튼 */}
            {approvalInput && (
              <button
                onClick={clearApproval}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>
                ×
              </button>
            )}
            {/* 자동완성 드롭다운 */}
            {dropdownOpen && (
              <ul style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 100,
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 6, margin: '2px 0 0', padding: 0,
                listStyle: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: 150,
              }}>
                {suggestions.map((s) => (
                  <li
                    key={s}
                    onMouseDown={() => selectSuggestion(s)}
                    style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--text)', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-subtle)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = ''}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
        }
      />

      <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 8 }}>
        총 {displayApps.length}건 (승인 합계 <strong>{fmtNum(total)}</strong>원)
        {/* 필터 적용 중일 때 안내 표시 */}
        {approvalFilter && (
          <span style={{ marginLeft: 8, color: 'var(--std-accent-text, #1d7de6)', fontWeight: 600 }}>
            승인번호 '{approvalFilter}' 필터 적용 중
          </span>
        )}
      </div>
      <div style={tblWrap}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['거래일시','마케터','카드사','카드번호','승인번호','거래금액','할부','수수료','입금예정액','입금예정일','정산상태','거래유형'].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayApps.length === 0 && (
              <tr>
                <td colSpan={12} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>
                  데이터 없음
                </td>
              </tr>
            )}
            {displayApps.map((r) => {
              const isMaechulBansong = r.revenue_type === '매출반송';
              const isGeojul         = r.transaction_type === '거절';
              // 취소: KIS 가 같은 승인번호로 내려주는 결제취소 — 정산·합계에서 제외되는 행 (2026-08-31)
              const isChwiso         = r.transaction_type === '취소';
              // 행 배경: 매출반송 > 거절 > 취소 > 기본
              const rowBg = isMaechulBansong ? '#fff7ed' : isGeojul ? '#fef2f2' : isChwiso ? '#f1f5f9' : 'transparent';

              return (
                <tr key={r.id} style={{ background: rowBg }}>
                  <td style={tdStyle}>{fmtDate(r.transaction_date)} {r.transaction_time || ''}</td>

                  {/* 마케터: terminalMap 우선 → user_name 폴백 → 단말기ID 주황 표시
                      파인그룹: terminalMap으로 매핑 / DOT·LNJ: API JOIN으로 반환된 user_name 사용 */}
                  <td style={tdStyle}>
                    {(() => {
                      const byMap = terminalMap[String(r.terminal_id || '').trim()];
                      const name  = byMap || r.user_name;
                      if (name) {
                        return (
                          <span>
                            {name}
                            <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--text-muted)' }}>
                              ({r.terminal_id})
                            </span>
                          </span>
                        );
                      }
                      return <span style={{ color: '#f59e0b', fontSize: 11 }}>{r.terminal_id || '미매칭'}</span>;
                    })()}
                  </td>

                  <td style={tdStyle}>{r.card_issuer || '-'}</td>
                  <td style={tdStyle}>{r.card_number || '-'}</td>
                  {/* 승인번호 */}
                  <td style={tdStyle}>{r.approval_no || '-'}</td>
                  <td style={amtTd}>{fmtNum(r.transaction_amount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {r.installment_months > 0 ? `${r.installment_months}개월` : '일시불'}
                  </td>
                  <td style={amtTd}>{fmtNum(r.fee)}</td>
                  <td style={amtTd}>{fmtNum(r.expected_payment_amount)}</td>
                  <td style={tdStyle}>{fmtDate(r.expected_payment_date)}</td>

                  {/* 정산상태: 매출반송은 주황 배지로 강조 */}
                  <td style={tdStyle}>
                    {isMaechulBansong
                      ? <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4,
                          background: '#fed7aa', color: '#9a3412', fontWeight: 600 }}>매출반송</span>
                      : (r.revenue_type || '-')
                    }
                  </td>

                  {/* 거래유형: 거절=빨강 / 취소=회색 / 승인=초록 배지 */}
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4,
                      background: isGeojul ? '#fee2e2' : isChwiso ? '#e2e8f0' : '#dcfce7',
                      color:      isGeojul ? '#991b1b' : isChwiso ? '#475569' : '#15803d' }}>
                      {r.transaction_type}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
